import {
  AppBrandLink,
  AppNavigationHeader,
  CollapsedRailIconButton,
  NavCollapseToggle,
  OverlayNavCloseToggle,
  ResizableAppNavigation,
  useIsLeftNavCollapseEnabled,
  useOverlayNavClose,
} from '@ms/yammer-features-app-navigation';
import { useTranslations } from '@ms/yammer-i18n-react';
import TeamsStrings from '@ms/yammer-strings/dist/Teams';
import { UniversalCreateButtonProvider } from '@ms/yammer-universal-create-button';
import { useCampaignCreateModal } from '@ms/yammer-web-components/dist/campaignCreateEdit/CampaignCreateModal/hooks/useCampaignCreateModal';
import { useGroupCreateModal } from '@ms/yammer-web-components/dist/groupCreateEdit/LoadableGroupCreateModal';
import NavigationCommunities from '@ms/yammer-web-components/dist/navigation/NavigationCommunities';
import { usePostCreateModal } from '@ms/yammer-web-components/dist/publisher/LoadableUniversalCreatePublisherModal';
import AutocompleteAriaHost from '@ms/yammer-web-core-ui/dist/components/AutocompleteAriaHost';
import FluentUISystemIcon, { SearchRegular } from '@ms/yammer-web-core-ui/dist/components/FluentUISystemIcon';
import FocusTwins from '@ms/yammer-web-core-ui/dist/util/accessibility/FocusTwins';
import { useIsTeamsPersonalApp } from 'hooks/selectors';
import type { FC, ReactNode } from 'react';
import React from 'react';

import AppSearchNavPhase1 from '../AppSearchNavPhase1';
import VivaSuiteNav from '../VivaSuiteNav';

import { useHomeLinkActionButtons } from './hooks/useHomeLinkActionButtons';

export interface DefaultLeftNavProps {
  readonly collapsedOverlayNav?: ReactNode;
  readonly userProfileLinkActionButtons: ReactNode[];
  readonly shouldFocusSearch?: boolean;
  readonly requestSearchFocus?: () => void;
  readonly consumeSearchFocus?: () => void;
}

interface CollapsedNavSearchButtonProps {
  readonly activateSearch: () => void;
}

const CollapsedNavSearchButton: FC<CollapsedNavSearchButtonProps> = ({ activateSearch }) => {
  const t = useTranslations(TeamsStrings);
  const searchLabel = t.vivaEngageAppSearchBoxSearchEngagePlaceholder();

  return (
    <CollapsedRailIconButton
      ariaLabel={searchLabel}
      icon={<FluentUISystemIcon Icon={SearchRegular} size={20} />}
      onClick={activateSearch}
    />
  );
};

export const DefaultLeftNav: FC<DefaultLeftNavProps> = ({
  collapsedOverlayNav,
  userProfileLinkActionButtons,
  shouldFocusSearch,
  requestSearchFocus,
  consumeSearchFocus,
}) => {
  const homeLinkActionButtons = useHomeLinkActionButtons();
  const isTeamsPersonalApp = useIsTeamsPersonalApp();
  const isLeftNavCollapseEnabled = useIsLeftNavCollapseEnabled();
  const onOverlayClose = useOverlayNavClose();
  const campaignCreateModal = useCampaignCreateModal('UniversalCreateButton');

  const showCollapseToggleInHeader = isLeftNavCollapseEnabled;
  const showSuiteNav = isTeamsPersonalApp && !isLeftNavCollapseEnabled;
  const collapseToggle = showCollapseToggleInHeader ? (
    <FocusTwins.Child>
      <NavCollapseToggle />
    </FocusTwins.Child>
  ) : null;
  const suiteNav = showSuiteNav ? <VivaSuiteNav /> : null;
  const headerContextMenu = onOverlayClose ? <OverlayNavCloseToggle /> : (collapseToggle ?? suiteNav);

  const isCollapseControlInHeader = showCollapseToggleInHeader || !!onOverlayClose;
  const renderCollapsedSearchButton = isTeamsPersonalApp
    ? (expandNavigation: () => void) => {
        const activateSearch = () => {
          requestSearchFocus?.();
          expandNavigation();
        };

        return <CollapsedNavSearchButton activateSearch={activateSearch} />;
      }
    : undefined;

  return (
    <UniversalCreateButtonProvider
      useGroupCreateModal={useGroupCreateModal}
      campaignCreateModal={campaignCreateModal}
      usePostCreateModal={usePostCreateModal}
    >
      <ResizableAppNavigation
        communitiesNavLinksContent={<NavigationCommunities />}
        collapseToggleInHeader={isCollapseControlInHeader}
        collapsedOverlayNav={collapsedOverlayNav}
        renderCollapsedAction={renderCollapsedSearchButton}
        renderHeader={(createButton) => (
          <AppNavigationHeader
            title={<AppBrandLink />}
            contextMenu={headerContextMenu}
            primaryAction={isLeftNavCollapseEnabled ? createButton : undefined}
            searchInput={
              <AutocompleteAriaHost>
                <AppSearchNavPhase1 mountWithFocus={shouldFocusSearch} consumeMountFocus={consumeSearchFocus} />
              </AutocompleteAriaHost>
            }
          />
        )}
        homeLinkActionButtons={homeLinkActionButtons}
        userProfileLinkActionButtons={userProfileLinkActionButtons}
      />
    </UniversalCreateButtonProvider>
  );
};
